import React, { useMemo, useState } from 'react';
import { SchemaGraph, SchemaNode } from './graphLoader';

/**
 * Table detail drawer for the Schema Items panel.
 *
 * Shows everything Neo4j knows about one table — description, primary key,
 * every field with data type + description, and FK relationships — rendered
 * entirely from the already-loaded graph data (no API call). This is how an
 * analyst answers questions like "what does booking.status = 'C' mean?"
 * without leaving the schema browser.
 */

interface FkRow {
  direction: 'out' | 'in';
  ownColumn: string;
  otherTable: string;
  otherColumn: string;
}

interface TableDetailDrawerProps {
  node: SchemaNode;
  graphData: SchemaGraph;
  /** Pre-filter the field list (set when the user clicked a field row). */
  initialFieldFilter?: string;
  onClose: () => void;
  /** Jump to another table's detail (FK targets are clickable). */
  onJumpToTable: (tableName: string) => void;
}

const LABEL: React.CSSProperties = { fontSize: 10, color: '#6b7280', fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.4 };
const TH: React.CSSProperties = {
  padding: '5px 8px', fontSize: 10, fontWeight: 600, color: '#374151',
  textAlign: 'left', borderBottom: '1px solid #e5e7eb', background: '#f9fafb',
  position: 'sticky', top: 0,
};
const TD: React.CSSProperties = { padding: '4px 8px', fontSize: 11, borderBottom: '1px solid #f3f4f6', verticalAlign: 'top' };

export function TableDetailDrawer({
  node, graphData, initialFieldFilter = '', onClose, onJumpToTable,
}: TableDetailDrawerProps) {
  const [fieldFilter, setFieldFilter] = useState(initialFieldFilter);

  const fqn = [node.database, node.schema, node.name].filter(Boolean).join('.');
  const pk = new Set((node.primaryKey ?? []).map((p) => p.toLowerCase()));

  // FK relationships: any non-containment edge touching this table.
  const nodeNameById = useMemo(() => {
    const m = new Map<string, string>();
    for (const n of graphData.nodes) m.set(n.id, n.name);
    return m;
  }, [graphData]);

  const fkRows = useMemo<FkRow[]>(() => {
    const rows: FkRow[] = [];
    for (const e of graphData.edges) {
      if (e.type === 'contains' || e.type === 'module_contains') continue;
      if (e.source === node.id) {
        rows.push({
          direction: 'out',
          ownColumn: e.sourceColumn || '?',
          otherTable: nodeNameById.get(e.target) || e.target,
          otherColumn: e.targetColumn || '?',
        });
      } else if (e.target === node.id) {
        rows.push({
          direction: 'in',
          ownColumn: e.targetColumn || '?',
          otherTable: nodeNameById.get(e.source) || e.source,
          otherColumn: e.sourceColumn || '?',
        });
      }
    }
    return rows;
  }, [graphData, node.id, nodeNameById]);

  const fkByColumn = useMemo(() => {
    const m = new Set<string>();
    for (const r of fkRows) if (r.direction === 'out') m.add(r.ownColumn.toLowerCase());
    return m;
  }, [fkRows]);

  const columns = node.columns ?? [];
  const visibleColumns = fieldFilter
    ? columns.filter((c) =>
        c.name.toLowerCase().includes(fieldFilter.toLowerCase()) ||
        (c.description || '').toLowerCase().includes(fieldFilter.toLowerCase()))
    : columns;

  return (
    <div style={{
      position: 'absolute', inset: 0, background: 'white', zIndex: 10,
      display: 'flex', flexDirection: 'column',
    }}>
      {/* Header */}
      <div style={{
        padding: '10px 14px', borderBottom: '1px solid #e5e7eb', flexShrink: 0,
        display: 'flex', alignItems: 'flex-start', gap: 10,
      }}>
        <button
          onClick={onClose}
          title="Back to list"
          style={{
            padding: '3px 10px', fontSize: 11, fontWeight: 500, cursor: 'pointer',
            border: '1px solid #d1d5db', borderRadius: 6, background: 'white', color: '#374151',
          }}
        >← Back</button>
        <div style={{ minWidth: 0, flex: 1 }}>
          <div style={{ fontWeight: 700, fontSize: 14, color: '#0369a1', fontFamily: 'monospace' }}>
            {node.name}
          </div>
          <div style={{ fontSize: 10, color: '#9ca3af', fontFamily: 'monospace' }}>{fqn}</div>
          {node.description && (
            <div style={{ fontSize: 11, color: '#374151', marginTop: 4 }}>{node.description}</div>
          )}
          <div style={{ display: 'flex', gap: 14, marginTop: 6, flexWrap: 'wrap' }}>
            {(node.modules?.length || node.module) && (
              <span style={{ fontSize: 10, color: '#1d4ed8' }}>
                <span style={LABEL}>Module{(node.modules?.length ?? 0) > 1 ? 's' : ''} </span>
                {node.modules?.length ? node.modules.join(', ') : node.module}
              </span>
            )}
            {(node.primaryKey?.length ?? 0) > 0 && (
              <span style={{ fontSize: 10, color: '#b45309' }}><span style={LABEL}>PK </span>{node.primaryKey!.join(', ')}</span>
            )}
            <span style={{ fontSize: 10, color: '#6b7280' }}>
              <span style={LABEL}>Fields </span>{columns.length}
              <span style={{ marginLeft: 10 }}><span style={LABEL}>FKs </span>{fkRows.length}</span>
            </span>
          </div>
        </div>
      </div>

      {/* Field filter */}
      <div style={{ padding: '6px 14px', borderBottom: '1px solid #f3f4f6', flexShrink: 0 }}>
        <input
          autoFocus
          value={fieldFilter}
          onChange={(e) => setFieldFilter(e.target.value)}
          placeholder="Filter fields by name or description… (e.g. status)"
          style={{
            width: '100%', padding: '5px 8px', border: '1px solid #d1d5db', borderRadius: 6,
            fontSize: 11, outline: 'none', boxSizing: 'border-box', color: '#111827',
          }}
        />
      </div>

      {/* Fields table */}
      <div style={{ flex: 1, overflowY: 'auto' }}>
        {visibleColumns.length === 0 ? (
          <div style={{ padding: 24, textAlign: 'center', color: '#9ca3af', fontSize: 12 }}>
            {columns.length === 0 ? 'No field metadata available for this table.' : 'No fields match the filter.'}
          </div>
        ) : (
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr>
                <th style={{ ...TH, width: 170 }}>Field</th>
                <th style={{ ...TH, width: 110 }}>Data Type</th>
                <th style={{ ...TH, width: 60 }}>Key</th>
                <th style={TH}>Description</th>
              </tr>
            </thead>
            <tbody>
              {visibleColumns.map((c) => {
                const lower = c.name.toLowerCase();
                const isPk = pk.has(lower);
                const isFk = fkByColumn.has(lower);
                return (
                  <tr key={c.name}>
                    <td style={{ ...TD, fontFamily: 'monospace', fontWeight: 600, color: '#111827' }}>{c.name}</td>
                    <td style={{ ...TD, fontFamily: 'monospace', color: '#6b7280' }}>
                      {c.dataType}{c.length ? `(${c.length})` : ''}
                    </td>
                    <td style={TD}>
                      {isPk && <span style={{ fontSize: 9, fontWeight: 700, color: '#b45309', background: '#fef3c7', borderRadius: 4, padding: '1px 5px', marginRight: 3 }}>PK</span>}
                      {isFk && <span style={{ fontSize: 9, fontWeight: 700, color: '#6d28d9', background: '#ede9fe', borderRadius: 4, padding: '1px 5px' }}>FK</span>}
                    </td>
                    <td style={{ ...TD, color: '#374151' }}>{c.description || <span style={{ color: '#d1d5db' }}>—</span>}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}

        {/* FK relationships */}
        {fkRows.length > 0 && (
          <div style={{ padding: '10px 14px 16px' }}>
            <div style={{ ...LABEL, marginBottom: 6 }}>Foreign-key relationships</div>
            {fkRows.map((r, i) => (
              <div key={i} style={{ fontSize: 11, fontFamily: 'monospace', padding: '2px 0', color: '#374151' }}>
                {r.direction === 'out' ? (
                  <>
                    <span style={{ color: '#0369a1' }}>{node.name}</span>.{r.ownColumn}
                    <span style={{ color: '#9ca3af' }}> → </span>
                    <button
                      onClick={() => onJumpToTable(r.otherTable)}
                      style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer', fontFamily: 'monospace', fontSize: 11, color: '#6d28d9', textDecoration: 'underline' }}
                    >{r.otherTable}</button>.{r.otherColumn}
                  </>
                ) : (
                  <>
                    <button
                      onClick={() => onJumpToTable(r.otherTable)}
                      style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer', fontFamily: 'monospace', fontSize: 11, color: '#6d28d9', textDecoration: 'underline' }}
                    >{r.otherTable}</button>.{r.otherColumn}
                    <span style={{ color: '#9ca3af' }}> → </span>
                    <span style={{ color: '#0369a1' }}>{node.name}</span>.{r.ownColumn}
                  </>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
